import { useEffect, useMemo, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const VARIANT_STYLES = {
	title: {
		baseColor: '#eb673b',
		fillColor: '#eb673b',
		fontSize: 'clamp(4rem, 4.4vw, 4rem)',
		fontWeight: 900,
		lineHeight: 1.04,
		letterSpacing: '-0.04em',
	},
	subtitle: {
		baseColor: 'rgba(255, 255, 255, 0.18)',
		fillColor: '#ffffff',
		fontSize: 'clamp(4rem, 2vw, 1.65rem)',
		fontWeight: 700,
		lineHeight: 1.42,
		letterSpacing: '-0.02em',
	},
};

const ScrollReveal = ({
	children,
	variant = 'subtitle',
	className = '',
	start = 'top 72%',
	end = 'bottom 28%',
	scrub = 1.1,
	baseColor,
	fillColor,
	style = {},
	fillRef,
	disableAnimation = false,
	baseVisible = false,
}) => {
	const containerRef = useRef(null);

	const splitText = useMemo(() => {
		if (typeof children !== 'string') {
			return children;
		}

		return children.split(/(\s+)/).map((word, index) => {
			if (word.match(/^\s+$/)) {
				return word;
			}

			return (
				<span className="scroll-reveal-word" key={`${word}-${index}`}>
					{word}
				</span>
			);
		});
	}, [children]);

	const resolvedVariant = VARIANT_STYLES[variant] || VARIANT_STYLES.subtitle;
	const resolvedBaseColor = baseColor || resolvedVariant.baseColor;
	const resolvedFillColor = fillColor || resolvedVariant.fillColor;

	useEffect(() => {
		if (disableAnimation) return undefined;

		const el = containerRef.current;
		if (!el) return;

		const fillLayer = el.querySelector('.scroll-reveal-fill');
		if (!fillLayer) return;

		const trigger = ScrollTrigger.create({
			trigger: el,
			start,
			end,
			scrub,
			animation: gsap.fromTo(
				fillLayer,
				{ clipPath: 'inset(0 100% 0 0)' },
				{ clipPath: 'inset(0 0% 0 0)', ease: 'none' }
			),
		});

		return () => {
			trigger.kill();
		};
	}, [start, end, scrub, disableAnimation]);

	const outerStyle = {
		position: 'relative',
		display: 'block',
		width: '100%',
		overflow: 'shown',
		...resolvedVariant,
		...style,
	};

	const baseStyle = {
		position: 'relative',
		zIndex: 1,
		display: 'block',
		margin: 0,
		color: resolvedBaseColor,
		width: '100%',
		visibility: baseVisible ? 'visible' : 'hidden',
	};

	const fillStyle = {
		position: 'absolute',
		inset: 0,
		zIndex: 2,
		display: 'block',
		margin: 0,
		color: resolvedFillColor,
		width: '100%',
		pointerEvents: 'none',
		clipPath: 'inset(0 100% 0 0)',
		visibility: 'visible',
	};

	return (
		<div ref={containerRef} className={`scroll-reveal ${className}`.trim()} style={outerStyle}>
			<span className="scroll-reveal-base" style={baseStyle}>
				{splitText}
			</span>
			<span ref={fillRef} className="scroll-reveal-fill" aria-hidden="true" style={fillStyle}>
				{splitText}
			</span>
		</div>
	);
};

export default ScrollReveal;
